import {
  createAdminUser as createUserApi,
  listAdminRole as listRolesApi,
  listAdminUser as listUsersApi,
  replaceRolesAdminUser as replaceRolesApi,
} from '@api/generated/sdk.gen';
import { Permissions } from '@shared/constants/permissions';

import type { AdminUserResponse, RoleResponse } from '@api/generated/types.gen';

/**
 * Семантические обёртки над сгенерированным SDK для раздела «Администраторы».
 *
 * Отдельной сущности «админ» на бэке нет: это пользователь, у которого хотя бы
 * одна роль несёт право `admin.access`. Список — `GET /api/admin/users?adminOnly=true`,
 * добавление — `POST /api/admin/users` (телефон + роли) либо, если человек уже
 * зарегистрирован в мобильном приложении, `PUT /api/admin/users/{id}/roles`.
 */

/** Страница списка администраторов: элементы и общее число для пагинации. */
export type AdminsPage = {
  items: AdminUserResponse[];
  total: number;
};

/** Страница администраторов (`adminOnly=true`), новые сверху. `page` — с единицы, как в antd Pagination. */
export async function fetchAdmins(page: number, size: number): Promise<AdminsPage> {
  const res = await listUsersApi({
    query: { page: page - 1, size, sort: ['createdAt,desc'], adminOnly: true },
  });
  return { items: res.data?.content ?? [], total: res.data?.totalElements ?? 0 };
}

/** Точечный поиск по телефону в E.164; `null`, если такого пользователя нет. */
export async function findUserByPhone(phoneE164: string): Promise<AdminUserResponse | null> {
  const res = await listUsersApi({ query: { phone: phoneE164, size: 1 } });
  return res.data?.content?.[0] ?? null;
}

/** Создаёт пользователя сразу с ролями (`POST /api/admin/users`); телефон уже в E.164. */
export async function createUserWithRoles(
  phoneE164: string,
  roleIds: string[],
): Promise<AdminUserResponse> {
  const res = await createUserApi({ body: { phone: phoneE164, roleIds } });
  if (!res.data) throw new Error('INVALID_RESPONSE');
  return res.data;
}

/** Полная замена ролей пользователя — вызывающий сам объединяет с текущими (см. `mergeRoleIds`). */
export async function replaceUserRoles(
  userId: string,
  roleIds: string[],
): Promise<AdminUserResponse> {
  const res = await replaceRolesApi({ path: { id: userId }, body: { roleIds } });
  if (!res.data) throw new Error('INVALID_RESPONSE');
  return res.data;
}

/** Роли, дающие вход в панель (несут `admin.access`). Только их имеет смысл выдавать здесь. */
export async function fetchAdminRoles(): Promise<RoleResponse[]> {
  const res = await listRolesApi();
  return (res.data ?? []).filter((r) => (r.permissions ?? []).includes(Permissions.ADMIN_ACCESS));
}
